import React, { useEffect, useRef, useState } from 'react'
import CreateDatasetProjectModal, { DatasetProjectFormData } from '../components/CreateDatasetProjectModal'
import { PERMISSIONS } from '../constants/permissions'
import CreateDatasetModal, { CreateDatasetPayload } from '../components/CreateDatasetModal'
import VersionTimeline from '../components/dataset/VersionTimeline'
import {
  formatBytes,
  formatLocalDate,
  renderSampling,
  renderOutput,
  renderSources,
  renderSourcePills,
  computeSplitPercents,
} from '../utils/datasetFormat'
import {
  childrenOfContainer,
  groupVersionsInContainer,
  inSameContainer,
  isChildOf,
  lineageChainOf,
} from '../utils/datasetTree'
import datasetService, { DatasetProjectActions } from '../services/datasetService'
import config from '../config'
import { trackPackaging, trackedKeys } from '../services/packagingWatcher'
import DatasetProgressModal from '../components/DatasetProgressModal'
import { formatLocaleDateOnly } from '../utils/date'
 

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

type NodeKind = 'project' | 'task' | 'dataset'

/**
 * 트리 노드 (프로젝트 / 작업 / 데이터셋 공용).
 *
 *  소속 = parent_id + parent_kind   (프로젝트 id 와 작업 id 가 겹칠 수 있어 둘을 함께 봐야 함)
 *  계보 = parent_node_id            (데이터셋 버전 사이의 "이전 버전". 소속과 무관)
 *
 * 직접 필드를 비교하지 말고 utils/datasetTree.ts 의 헬퍼를 쓴다. CLAUDE.md 참고.
 */
type DatasetNode = {
  id: number
  name: string
  kind: NodeKind
  parent_id?: number | null
  parent_kind?: 'project' | 'task'
  // 버전 계보상 이전 버전 (null = 독립 데이터셋)
  parent_node_id?: number | null
  description?: string | null
  created_at?: string
  actions?: DatasetProjectActions
}

// CreateDatasetModal 오픈 타겟: 기존 데이터셋 재생성(rebuild) 또는 모델 하위 신규 생성(create)
type BuildTarget =
  | { mode: 'rebuild'; nodeId: number }
  | { mode: 'create'; projectId: number; taskId: number }

export default function DatasetManagement({ userInfo }: Props) {
  const [nodes, setNodes] = useState<DatasetNode[]>([])
  // kind가 다르면 id가 겹칠 수 있으므로 `${kind}-${id}` 문자열 키 사용
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [selected, setSelected] = useState<DatasetNode | null>(null)
  const [descExpanded, setDescExpanded] = useState<boolean>(false)
  const [isCreateOpen, setCreateOpen] = useState<boolean>(false)
  const [buildTarget, setBuildTarget] = useState<BuildTarget | null>(null)
  // 모델 생성 인라인 모달
  const [taskModal, setTaskModal] = useState<{ open: boolean; projectId: number; name: string; description: string; submitting: boolean }>({ open: false, projectId: 0, name: '', description: '', submitting: false })
  const [activeJob, setActiveJob] = useState<{ jobId: number | null; status: string; step?: string; progress?: number; download_url?: string; error?: string | null } | null>(null)
  const [latest, setLatest] = useState<{ ready: boolean; zip_ready: boolean; zip_expires_at?: string; zip_size?: number; download_url?: string; reason?: 'none' | 'lost' | 'building' | 'restore_failed' } | null>(null)
  const [summary, setSummary] = useState<any | null>(null)
  // 진행 모달 오픈 상태/폴링 제어용
  const [isProgressOpen, setProgressOpen] = useState<boolean>(false)
  const pollStopRef = useRef<boolean>(false)
  const pollTimerRef = useRef<number | null>(null)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  // ZIP 캐시 만료 시 스토어에서 재조립 중 상태 (완료되면 자동으로 다운로드 재시도)
  const [isRestoring, setIsRestoring] = useState<boolean>(false)
  // 재조립 진행률 0~99 (서버가 아직 총량을 모르는 초반에는 null)
  const [restoreProgress, setRestoreProgress] = useState<number | null>(null)
  const restoreCancelRef = useRef<boolean>(false)

  // 재조립 감시(폴링·완료 알림·자동 다운로드)는 전역 packagingWatcher 가 담당한다 —
  // 화면을 떠나거나 새로고침해도 완료 시 알림과 다운로드가 나간다.
  // 여기서는 감시자에 등록하고, 이벤트를 받아 준비 중 표시만 갱신한다.
  const startRestoreTracking = (nodeId: number, nodeName: string, filename: string, pollingUrl?: string) => {
    setIsRestoring(true)
    setRestoreProgress(null)
    const token = localStorage.getItem('access_token') || ''
    const pu = pollingUrl || `/datasets/${nodeId}/restore-status`
    const statusUrl = pu.startsWith('http') ? pu : `${config.API_BASE_URL}${pu}`
    const dp = new URLSearchParams()
    if (filename) dp.set('filename', filename)
    if (token) dp.set('access_token', token)
    trackPackaging(`dataset:${nodeId}`, {
      statusUrl,
      downloadUrl: `${config.API_BASE_URL}/datasets/${nodeId}/download?${dp.toString()}`,
      ts: Date.now(),
      kind: '학습데이터셋',
      name: [nodeName, filename ? `${filename}.zip` : ''].filter(Boolean).join('\n'),
    })
  }
  useEffect(() => {
    // 마운트 시 재조립이 진행 중이면 준비 중 표시 복원
    if (trackedKeys('dataset:').length > 0) setIsRestoring(true)
    const onProgress = (e: Event) => {
      const { key, progress } = ((e as CustomEvent).detail || {}) as { key?: string; progress?: number }
      if (typeof key !== 'string' || !key.startsWith('dataset:')) return
      setIsRestoring(true)
      if (typeof progress === 'number') setRestoreProgress(progress)
    }
    const onDone = (e: Event) => {
      const { key } = ((e as CustomEvent).detail || {}) as { key?: string }
      if (typeof key !== 'string' || !key.startsWith('dataset:')) return
      setIsRestoring(false)
      setRestoreProgress(null)
    }
    window.addEventListener('packaging:progress', onProgress)
    window.addEventListener('packaging:done', onDone)
    return () => {
      window.removeEventListener('packaging:progress', onProgress)
      window.removeEventListener('packaging:done', onDone)
    }
  }, [])
  // 버전 계보: 생성 시 선언된 "이전 버전"과의 차이 (선언 없으면 null)
  const [lineage, setLineage] = useState<Awaited<ReturnType<typeof datasetService.getLineage>>>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  // 임의 두 버전 비교 (라벨 단위 diff 는 서버가 백그라운드로 계산 → queued/running 이면 폴링)
  const [compare, setCompare] = useState<{
    baseId: number | null
    loading: boolean
    result: Awaited<ReturnType<typeof datasetService.compareVersions>>
    error?: string
  }>({ baseId: null, loading: false, result: null })
  // 클래스별 분포 (지표/통계 탭) — 모든 라벨 포맷 지원
  const [classStats, setClassStats] = useState<Awaited<ReturnType<typeof datasetService.getClassStats>>>(null)
  // 상세 탭 (조감도: 요약 / 변경 파일 / 지표·통계 / DVC / 다운로드·사용)
  const [activeTab, setActiveTab] = useState<'summary' | 'changes' | 'stats' | 'dvc' | 'download'>('summary')
  // 프로젝트 삭제 확인 (하위 전체가 사라지므로 이름 입력을 요구한다)
  const [confirmProjectDelete, setConfirmProjectDelete] = useState<{ open: boolean; project: DatasetNode | null; typed: string }>(
    { open: false, project: null, typed: '' },
  )
  const [isDeletingProject, setIsDeletingProject] = useState<boolean>(false)
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; node: DatasetNode | null; typed: string }>({ open: false, node: null, typed: '' })
  

  // 다운로드 진행 시 전역 커서를 'wait'로 변경했다가 복구
  useEffect(() => {
    if (isDownloading) {
      document.body.style.cursor = 'wait'
    } else {
      document.body.style.cursor = ''
    }
    return () => { document.body.style.cursor = '' }
  }, [isDownloading])

  // 초기 로드: 서버에서 트리 로드
  useEffect(() => {
    ;(async () => { await loadProjects() })()
  }, [])

  const loadProjects = async () => {
    try {
      const list = await datasetService.listProjects()
      // API 응답과 로컬 타입이 호환되어 바로 세팅 가능
      setNodes(list as unknown as DatasetNode[])
      return list as unknown as DatasetNode[]
    } catch {
      setNodes([])
      return [] as DatasetNode[]
    }
  }

  // 노드 배열 중복 제거: 같은 숫자 id라도 kind가 다르면 다른 노드로 취급
  useEffect(() => {
    if (!nodes || nodes.length === 0) return
    const map = new Map<string, DatasetNode>()
    let changed = false
    for (const n of nodes) {
      const key = `${n.kind}-${n.id}`
      if (!map.has(key)) map.set(key, n)
      else changed = true
    }
    const unique = Array.from(map.values())
    if (changed || unique.length !== nodes.length) {
      setNodes(unique)
    }
  }, [nodes])

  // 부모-자식 판정 유틸: kind가 다르면 id가 겹칠 수 있으므로 parent_kind까지 확인
  // 트리는 프로젝트 > 모델(작업) 2단계까지만 표현한다.
  // 데이터셋(버전)은 모델을 선택했을 때 2열에서 계열별로 보여준다 — 폴더로 늘어놓으면
  // 계보로 이어진 버전과 독립 데이터셋이 같은 높이에 섞여 혼동된다.
  // 트리는 프로젝트 > 작업 2단계까지만 (데이터셋은 작업 화면에서 계열별로 표시)
  const childrenOf = (parent: DatasetNode) =>
    parent.kind === 'project' ? childrenOfContainer(nodes, parent, 'task') : []

  // 모델(또는 프로젝트 직속) 아래 데이터셋을 계보 계열별로 묶는다.
  // 반환: [{ head: 계열의 최신 노드, items: 최신→과거 순 }], 그리고 독립 목록
  const groupVersions = (parent: DatasetNode) => groupVersionsInContainer(nodes, parent)

  // 선택된 데이터셋 노드에 대해 최신 요약/상세를 로드
  useEffect(() => {
    // 노드 전환 시 진행 중이던 재조립 폴링 중단
    restoreCancelRef.current = true
    setIsRestoring(false); setRestoreProgress(null)
    ;(async () => {
      if (selected && isDatasetLeaf(selected)) {
        try {
          const l = await datasetService.getLatestSummary(selected.id)
          setLatest(l)
          if (l?.ready) {
            try { setSummary(await datasetService.getSummary(selected.id)) } catch { setSummary(null) }
          } else {
            setSummary(null)
          }
        } catch {
          setLatest(null)
          setSummary(null)
        }
        try { setLineage(await datasetService.getLineage(selected.id)) } catch { setLineage(null) }
      } else {
        setLatest(null)
        setSummary(null)
        setLineage(null)
      }
    })()
  }, [selected])

  // 언마운트 시 재조립 폴링 중단
  useEffect(() => () => { restoreCancelRef.current = true }, [])

  const checkDuplicateLocal = async (data: { projectName: string; taskName?: string }) => {
    const project = nodes.find(n => n.kind === 'project' && n.name === data.projectName && (n.parent_id == null))
    if (data.taskName == null || data.taskName.trim().length === 0) {
      return project ? 'duplicateProject' : 'ok'
    }
    if (!project) return 'ok'
    const pairExists = childrenOf(project).some(n => n.kind === 'task' && n.name === data.taskName)
    return pairExists ? 'duplicatePair' : 'ok'
  }

  const handleCreateProject = async (form: DatasetProjectFormData) => {
    // 1단계(프로젝트) 생성 후, 작업명이 있으면 2단계(모델)까지 만든다.
    // 데이터셋(3단계)은 여기서 만들지 않는다 — 모델 화면의 「+ 새 데이터셋 버전」이 담당.
    const res = await datasetService.createProject({
      projectName: form.projectName,
      projectDescription: form.projectDescription,
      existingProjectId: form.existingProjectId,
    })
    let taskId: number | null = null
    const taskName = (form.taskName || '').trim()
    if (taskName) {
      const m = await datasetService.createTask(res.project_id, {
        name: taskName,
        description: (form.taskDescription || '').trim() || undefined,
      })
      taskId = m.task_id
    }
    const list = await loadProjects()
    if (taskId != null) {
      setExpanded(prev => { const ns = new Set(prev); ns.add(`project-${res.project_id}`); return ns })
      setSelected((list.find(n => n.kind === 'task' && n.id === taskId) || null) as any)
    } else {
      setSelected((list.find(n => n.kind === 'project' && n.id === res.project_id) || null) as any)
    }
    setCreateOpen(false)
  }

  const handleCreateTask = async () => {
    const name = taskModal.name.trim()
    if (!name || taskModal.submitting) return
    try {
      setTaskModal(prev => ({ ...prev, submitting: true }))
      const res = await datasetService.createTask(taskModal.projectId, {
        name,
        description: taskModal.description.trim().length > 0 ? taskModal.description.trim() : undefined,
      })
      const list = await loadProjects()
      setExpanded(prev => { const ns = new Set(prev); ns.add(`project-${taskModal.projectId}`); return ns })
      const task = list.find(n => n.kind === 'task' && n.id === res.task_id) || null
      if (task) { setSelected(task as any); setDescExpanded(false) }
      setTaskModal({ open: false, projectId: 0, name: '', description: '', submitting: false })
    } catch (e: any) {
      alert(e?.message || '작업 생성에 실패했습니다.')
      setTaskModal(prev => ({ ...prev, submitting: false }))
    }
  }

  const renderNode = (node: DatasetNode, level = 0, visited: Set<string> = new Set()) => {
    const visitKey = `${node.kind}-${node.id}`
    const isExpanded = expanded.has(visitKey)
    if (visited.has(visitKey)) return null
    const nextVisited = new Set(visited)
    nextVisited.add(visitKey)
    const children = childrenOf(node)
      .filter((n, idx, arr) => idx === arr.findIndex(a => (a.id === n.id && a.kind === n.kind)))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    const hasChildren = children.length > 0
    return (
      <div key={`${node.kind}-${node.id}`}>
        <div
          className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${
            (selected?.id === node.id && selected?.kind === node.kind) ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            setSelected(node); setDescExpanded(false)
            if (hasChildren && !expanded.has(visitKey)) {
              setExpanded(prev => { const ns = new Set(prev); ns.add(visitKey); return ns })
            }
          }}
        >
          {/* 폴더 아이콘 — flex-shrink-0 없으면 긴 이름(공백 없는 토큰)이 아이콘을 0px로 수축시킨다 */}
          <svg
            className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>

          {/* 확장/축소 화살표 */}
          {hasChildren && (
            <button
              onClick={(e) => { e.stopPropagation(); const ns = new Set(expanded); ns.has(visitKey) ? ns.delete(visitKey) : ns.add(visitKey); setExpanded(ns) }}
              className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
            >
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
            {node.name}
            {hasChildren ? ` (${children.length})` : ''}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {children.map(c => renderNode(c, level + 1, nextVisited))}
          </div>
        )}
      </div>
    )
  }

  const isDatasetLeaf = (node: DatasetNode | null) => node?.kind === 'dataset'

  // 새 DatasetProject 생성은 전역 권한(dataset_project_manage)으로만 허용,
  // 하지만 기존 프로젝트에 학습데이터셋을 생성(create 액션)할 수 있는 경우에도
  // 동일 모달을 열 수 있어야 하므로, "버튼 노출" 기준은 두 가지를 모두 본다.
  const hasGlobalDatasetProjectManage =
    userInfo.isAdmin || userInfo.permissions.includes(PERMISSIONS.DATASET_PROJECT_MANAGE)
  const hasCreateOnAnyProject =
    nodes.some(n => n.kind === 'project' && !!n.actions?.create)
  const canCreateProject = hasGlobalDatasetProjectManage || hasCreateOnAnyProject

  const getNodeActions = (node: DatasetNode | null): DatasetProjectActions => {
    if (!node) return {}
    if (userInfo.isAdmin) {
      return {
        view: true,
        build: true,
        download: true,
        delete: true,
        create: true,
      }
    }
    return node.actions || {}
  }

  const selectedActions = getNodeActions(selected)

  // CreateDatasetModal의 "이전 버전" 후보: 같은 부모(모델 또는 미분류) 아래의 데이터셋들 (rebuild면 자기 제외)
  const buildSiblings = (() => {
    if (!buildTarget) return []
    if (buildTarget.mode === 'create') {
      return nodes
        .filter(n => n.kind === 'dataset' && n.parent_kind === 'task' && n.parent_id === buildTarget.taskId)
        .map(n => ({ id: n.id, name: n.name }))
    }
    const target = nodes.find(n => n.kind === 'dataset' && n.id === buildTarget.nodeId)
    if (!target) return []
    return nodes
      .filter(n => n.kind === 'dataset' && n.id !== target.id && inSameContainer(n, target))
      .map(n => ({ id: n.id, name: n.name }))
  })()

  // 선택된 데이터셋과 같은 계열(같은 모델, 미분류면 같은 프로젝트 직속)의 버전들 — 최신순
  // 같은 버전 계열 = parent_node_id 로 이어진 계보 사슬.
  // 같은 모델 아래에 있어도 계보가 연결되지 않은 데이터셋(독립)은 포함하지 않는다.
  // 버전 히스토리 = 계보 사슬 (소속과 무관)
  const siblingVersions = (isDatasetLeaf(selected) && selected) ? lineageChainOf(selected, nodes) : []

  // 비교 대상 후보는 계열과 무관하게 같은 모델(미분류면 프로젝트 직속) 아래 전부.
  // 계보가 없어도 "그때 데이터와 뭐가 다른지" 비교하고 싶을 수 있다.
  // 「현재 버전」 = 계보상 가장 최근 버전
  const currentVersion = siblingVersions[0] || null

  const comparableVersions = (isDatasetLeaf(selected) && selected)
    ? nodes
        .filter(n => n.kind === 'dataset' && n.id !== selected.id && inSameContainer(n, selected))
        .filter((n, i, arr) => i === arr.findIndex(a => a.id === n.id))
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    : []

  const canBuildSelected = !!(isDatasetLeaf(selected) && selectedActions.build)
  const canDeleteSelected = !!(isDatasetLeaf(selected) && latest?.ready && selectedActions.delete)
  const canDownloadSelected = !!(isDatasetLeaf(selected) && latest?.ready && selectedActions.download)

  // ── 조감도 레이아웃용 파생값 ──────────────────────────────
  const TABS = [
    { key: 'summary' as const, label: '요약' },
    { key: 'changes' as const, label: '변경 파일' },
    { key: 'stats' as const, label: '지표/통계' },
    { key: 'dvc' as const, label: 'DVC' },
    { key: 'download' as const, label: '다운로드' },
  ]

  // 「+ 새 데이터셋 버전」 버튼의 대상 (작업이 정해져야 생성 가능)
  const newVersionTarget: BuildTarget | null = (() => {
    if (!selected) return null
    if (selected.kind === 'task' && selectedActions.create) {
      return { mode: 'create', projectId: selected.parent_id ?? 0, taskId: selected.id }
    }
    if (selected.kind === 'dataset' && selected.parent_kind === 'task' && selectedActions.create) {
      const task = nodes.find(n => n.kind === 'task' && n.id === selected.parent_id)
      if (task) return { mode: 'create', projectId: task.parent_id ?? 0, taskId: task.id }
    }
    return null
  })()

  const dvcContentHash: string | null = (summary as any)?.content_hash || null

  // 비교 실행 — 라벨 단위 diff 가 계산 중이면 완료까지 폴링한다
  const runCompare = async (baseId: number) => {
    if (!selected) return
    setCompare({ baseId, loading: true, result: null })
    try {
      let res = await datasetService.compareVersions(selected.id, baseId)
      setCompare({ baseId, loading: false, result: res })
      let tries = 0
      while (res && (res.label_diff_status === 'queued' || res.label_diff_status === 'running') && tries < 40) {
        await new Promise(r => window.setTimeout(r, 2000))
        if (!selected) return
        res = await datasetService.compareVersions(selected.id, baseId)
        setCompare({ baseId, loading: false, result: res })
        tries += 1
      }
    } catch (e) {
      setCompare({ baseId, loading: false, result: null, error: (e as Error)?.message || '비교에 실패했습니다' })
    }
  }

  // 「변경 파일」 탭의 기본 비교 대상 = 직전 버전.
  // 생성 시 선언된 "이전 버전"이 있으면 그것을, 없으면 시간상 바로 앞 버전을 쓴다.
  // 첫 버전이라 앞이 없으면 null → 탭에서 "비교할 다른 버전이 없습니다" 를 보여준다.
  const defaultCompareBaseId: number | null = (() => {
    if (!selected || !isDatasetLeaf(selected)) return null
    if (lineage?.parent?.id) return lineage.parent.id
    const idx = siblingVersions.findIndex(v => v.id === selected.id)
    // siblingVersions 는 최신순이므로 다음 원소가 직전 버전
    const prev = idx >= 0 ? siblingVersions[idx + 1] : undefined
    return prev ? prev.id : null
  })()

  // 선택이 바뀌면 이전 비교 결과를 버리고 탭을 요약으로 되돌린다.
  // (탭이 유지되면 비교하다가 새 데이터셋을 만들었을 때 첫 화면이 「변경 파일」로 열린다)
  useEffect(() => {
    setCompare({ baseId: null, loading: false, result: null })
    setActiveTab('summary')
  }, [selected?.id])

  // 「변경 파일」 탭을 열면 직전 버전과 자동으로 비교한다
  useEffect(() => {
    if (activeTab !== 'changes') return
    if (!selected || !isDatasetLeaf(selected) || !latest?.ready) return
    if (compare.baseId || compare.loading || compare.result) return
    if (!defaultCompareBaseId) return
    runCompare(defaultCompareBaseId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selected?.id, latest?.ready, defaultCompareBaseId])

  // 지표/통계 탭을 열면 클래스 분포를 로드한다 (무거우면 queued → 폴링)
  useEffect(() => {
    if (!(activeTab === 'stats' && selected && isDatasetLeaf(selected) && latest?.ready)) return
    let cancelled = false
    const nodeId = selected.id
    ;(async () => {
      setClassStats(null)
      let res = await datasetService.getClassStats(nodeId)
      if (cancelled) return
      setClassStats(res)
      let tries = 0
      while (!cancelled && res && (res.status === 'queued' || res.status === 'running') && tries < 40) {
        await new Promise(r => window.setTimeout(r, 2000))
        if (cancelled) return
        res = await datasetService.getClassStats(nodeId)
        setClassStats(res)
        tries += 1
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selected?.id, latest?.ready])

  const handleDownload = async () => {
    if (!selected) return
    try {
      const suggested = `${(selected.name || 'dataset')}`
      const input = window.prompt('다운로드할 파일명을 입력하세요 (확장자 없이)', suggested)
      if (input === null) return
      const cleaned = (input || '').trim().replace(/[\\/\0]/g, '')
      if (cleaned.length === 0) {
        alert('유효한 파일명을 입력하세요.')
        return
      }
      setIsDownloading(true)
      const res = await datasetService.download(selected.id, cleaned)
      if (res.status === 'processing') {
        // ZIP 캐시가 만료·삭제됨 → 스토어에서 재조립 중.
        // 전역 감시자가 완료를 지켜보다 알림 + 자동 다운로드한다 (화면을 떠나도 유지)
        startRestoreTracking(selected.id, selected.name || 'dataset', cleaned, res.polling_url)
      }
    } catch (e) {
      alert((e as Error)?.message || '데이터셋 다운로드에 실패했습니다.')
    } finally {
      window.setTimeout(() => setIsDownloading(false), 1200)
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* 페이지 헤더 */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">학습 데이터셋 관리</h1>
        <p className="text-gray-600">DVC(Data Version Control)로 버전이 관리되는 학습 데이터셋을 생성하고 다운로드합니다.</p>
      </div>

      <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* ── 1열: 데이터셋 구조 ───────────────────────────────── */}
        <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50">
          <div className="p-4 min-h-16 border-b border-gray-200 bg-white flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">데이터셋 구조</h3>
            {canCreateProject && (
              <button
                onClick={() => setCreateOpen(true)}
                className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
              >
                + 프로젝트 생성
              </button>
            )}
          </div>
        <div className="p-2 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
          {(() => {
            const roots = nodes
              .filter(n => n.kind === 'project' && n.parent_id == null)
              .filter((n, idx, arr) => idx === arr.findIndex(a => (a.id === n.id && a.kind === n.kind)))
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
            if (roots.length === 0) {
              return (
                <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 350px)' }}>
                  <div className="text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm mb-2">등록된 학습 데이터셋 프로젝트가 없습니다</p>
                    <p className="text-xs text-gray-400 mb-3">새로운 프로젝트를 생성해주세요</p>
                    {canCreateProject && (
                    <button
                      onClick={() => setCreateOpen(true)}
                      className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                    >
                      프로젝트 생성
                    </button>
                    )}
                  </div>
                </div>
              )
            }
            return roots.map(r => renderNode(r))
          })()}
        </div>
        </div>

        {/* ── 2열: 상세 ───────────────────────────────────────── */}
        <div className="flex-1 min-w-0 bg-white flex flex-col">
          {/* 상세 헤더 — 선택한 노드의 이름과 그 노드에서 할 수 있는 일만 둔다.
              (전역 진입점인 「프로젝트 생성」은 좌측 트리 헤더에 있다) */}
          <div className="p-4 min-h-16 border-b border-gray-200 flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 break-all">
                {selected ? selected.name : '노드를 선택하세요'}
              </h3>
              {selected && isDatasetLeaf(selected) && currentVersion?.id === selected.id && (
                <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">최신 버전</span>
              )}
            </div>
            <div className="flex space-x-2 flex-shrink-0">
              {selected?.kind === 'project' && selectedActions.create && (
                <button
                  onClick={() => setTaskModal({ open: true, projectId: selected.id, name: '', description: '', submitting: false })}
                  className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                >
                  + 작업 생성
                </button>
              )}
              {/* 프로젝트 삭제 — 평가 데이터셋과 동일하게 관리자 전용 */}
              {selected?.kind === 'project' && userInfo.isAdmin && (
                <button
                  onClick={() => setConfirmProjectDelete({ open: true, project: selected, typed: '' })}
                  className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded bg-white hover:bg-red-50"
                >
                  프로젝트 삭제
                </button>
              )}
              {newVersionTarget && (
                <button
                  onClick={() => setBuildTarget(newVersionTarget)}
                  className="px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                >
                  + 새 데이터셋 버전
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto" style={{ height: 'calc(100vh - 260px)' }}>
          {!selected && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p className="text-lg font-medium">노드를 선택하세요</p>
                <p className="text-sm">좌측의 데이터셋 구조에서 노드를 클릭하세요.</p>
              </div>
            </div>
          )}

          {selected && selected.kind === 'project' && (
            <div className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm text-gray-700">작업</div>
              </div>
              {(() => {
                const children = childrenOf(selected)
                  .filter((n, idx, arr) => idx === arr.findIndex(a => (a.id === n.id && a.kind === n.kind)))
                  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                const tasks = children.filter(n => n.kind === 'task')
                // childrenOf 는 이제 모델만 돌려준다(트리 2단계). 프로젝트 직속
                // 미분류 데이터셋은 별도로 구해야 화면에서 사라지지 않는다.
                const legacy = groupVersions(selected)
                return (
                  <>
                    {tasks.length === 0 ? (
                      <div className="text-sm text-gray-500">등록된 작업이 없습니다.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {tasks.map(m => (
                          <div
                            key={`task-${m.id}`}
                            className="border rounded hover:bg-gray-50 cursor-pointer p-3"
                            onClick={() => { setSelected(m); setDescExpanded(false) }}
                          >
                            <div className="font-medium text-gray-900">{m.name}</div>
                            <div className="text-xs text-gray-500 mt-1 overflow-hidden text-ellipsis whitespace-nowrap">{(m.description && m.description.trim().length > 0) ? m.description : '설명 없음'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {legacy.total > 0 && (
                      <div className="mt-6">
                        <div className="mb-2 text-sm text-gray-700">미분류 데이터셋</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[...legacy.chains.flat(), ...legacy.standalone].map(ds => (
                            <div
                              key={`ds-${ds.id}`}
                              className="border rounded hover:bg-gray-50 cursor-pointer p-3"
                              onClick={() => { setSelected(ds); setDescExpanded(false) }}
                            >
                              <div className="font-medium text-gray-900">{ds.name}</div>
                              <div className="text-xs text-gray-500 mt-1 overflow-hidden text-ellipsis whitespace-nowrap">{(ds.description && ds.description.trim().length > 0) ? ds.description : '설명 없음'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          )}
          {selected && selected.kind === 'task' && (
            <div className="p-4">
                {/* 이름은 상세 헤더에 있으므로 여기서는 설명만 */}
                <p className="text-sm text-gray-500 mb-4">
                  {(selected.description && selected.description.trim()) || '설명 없음'}
                </p>

                {(() => {
                  const { chains, standalone, total } = groupVersions(selected)
                  if (total === 0) {
                    return (
                      <div className="border border-dashed rounded p-8 text-center">
                        <p className="text-gray-900 font-medium">아직 데이터셋 버전이 없습니다</p>
                        <p className="text-sm text-gray-500 mt-1">「+ 새 데이터셋 버전」으로 첫 버전을 만들어보세요.</p>
                      </div>
                    )
                  }
                  return (
                    <div className="space-y-4">
                      {chains.map((chain, ci) => (
                        <div key={`chain-${chain[0]?.id ?? ci}`} className="border rounded p-4">
                          <div className="text-xs font-medium text-gray-600 mb-2">
                            버전 계열{chains.length > 1 ? ` ${ci + 1}` : ''}
                            <span className="text-gray-400 ml-1.5">{chain.length}개</span>
                          </div>
                          <VersionTimeline
                            versions={chain}
                            currentId={chain[0]?.id ?? null}
                            onSelect={(v) => { setSelected(v); setDescExpanded(false) }}
                          />
                        </div>
                      ))}
                      {standalone.length > 0 && (
                        <div className="border rounded p-4">
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            독립<span className="text-gray-400 ml-1.5">{standalone.length}개</span>
                          </div>
                          <div className="text-xs text-gray-400 mb-2">이전 버전을 선언하지 않아 계보가 없는 데이터셋입니다.</div>
                          <VersionTimeline
                            versions={standalone}
                            currentId={null}
                            onSelect={(v) => { setSelected(v); setDescExpanded(false) }}
                          />
                        </div>
                      )}
                    </div>
                  )
                })()}
            </div>
          )}
          {selected && isDatasetLeaf(selected) && (
            <div className="p-4">
              {/* 제목과 「최신 버전」 배지는 상세 헤더에 있다 */}
              {/* 메타 정보 */}
              <div className="flex items-center gap-x-5 gap-y-1 flex-wrap text-sm">
                <span className="text-gray-500">생성일 <span className="text-gray-900 ml-1">{summary?.created_at ? formatLocalDate(summary.created_at) : (selected.created_at ? formatLocalDate(selected.created_at) : '-')}</span></span>
                <span className="text-gray-500">생성자 <span className="text-gray-900 ml-1">{summary?.created_by_name || summary?.created_by_email || '-'}</span></span>
                <span className="text-gray-500">
                  기반 버전
                  {lineage?.parent
                    ? <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-xs">{lineage.parent.name}</span>
                    : <span className="text-gray-400 ml-1">없음</span>}
                </span>
                <span className="text-gray-500">
                  DVC 상태
                  {dvcContentHash
                    ? <span className="ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs">tracked</span>
                    : <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">미추적</span>}
                </span>
              </div>

              {/* 설명 — 가장 위에 표시 */}
              <div className="mt-3 border rounded px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-sm font-semibold text-gray-900">설명</div>
                  <button
                    type="button"
                    className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                    onClick={() => setDescExpanded(v => !v)}
                    aria-expanded={descExpanded}
                  >
                    {descExpanded ? '접기' : '더보기'}
                  </button>
                </div>
                <div className={`text-sm whitespace-pre-wrap break-words leading-relaxed ${descExpanded ? '' : 'max-h-24 overflow-hidden'}`}>
                  {selected.description && String(selected.description).trim().length > 0
                    ? <span className="text-gray-700">{selected.description}</span>
                    : <span className="text-gray-400 italic">입력한 내용이 없습니다</span>}
                </div>
              </div>

              {/*
                진행 중 작업 표시는 여기에 두지 않는다 — DatasetProgressModal 이
                같은 activeJob 으로 단계·진행률을 보여주므로 같은 내용이 두 곳에
                뜬다. 모달을 닫아도 알 수 있어야 한다면 상단 상태 배지로 만들 것.
              */}

              {/* 탭 */}
              <div className="mt-4 border-b border-gray-200 flex gap-5">
                {TABS.map(t => (
                  <button
                    key={t.key}
                    className={`pb-2 -mb-px text-sm transition-colors ${
                      activeTab === t.key
                        ? 'border-b-2 border-gray-900 text-gray-900 font-semibold'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {!latest?.ready && (
                <div className={`mt-6 border border-dashed rounded p-8 text-center ${
                  latest?.reason === 'lost' ? 'border-amber-300 bg-amber-50' : ''
                }`}>
                  {latest?.reason === 'lost' ? (
                    <>
                      <p className="text-amber-900 font-medium">산출물이 남아 있지 않습니다</p>
                      <p className="text-sm text-amber-800 mt-1">
                        DVC 스토어 도입 이전에 만들어진 데이터셋입니다. ZIP 캐시가 만료·삭제되었고
                        스토어에도 보존되지 않아 복구할 수 없습니다 — 같은 조건으로 다시 만들어야 합니다.
                      </p>
                    </>
                  ) : latest?.reason === 'building' ? (
                    <p className="text-gray-900 font-medium">생성이 진행 중입니다</p>
                  ) : (
                    <p className="text-gray-900 font-medium">아직 생성된 학습데이터셋이 없습니다</p>
                  )}
                  {canBuildSelected && latest?.reason !== 'building' && (
                    <button
                      className="mt-3 px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                      onClick={() => { if (selected) setBuildTarget({ mode: 'rebuild', nodeId: selected.id }) }}
                    >
                      {latest?.reason === 'lost' ? '같은 조건으로 다시 생성' : '학습데이터셋 생성'}
                    </button>
                  )}
                </div>
              )}

              {/* 요약 탭 */}
              {latest?.ready && activeTab === 'summary' && (
                <div className="mt-4">
                  {summary && (
                    <div className="mt-6 border rounded p-6">
                    <div className="text-base font-semibold text-gray-900 mb-4">데이터셋 요약</div>
                    {/* 총 개수 + 분할 막대 (세로 공간 넉넉히) */}
                    <div className="mb-6">
                      <div className="flex items-end gap-3">
                        <div className="text-3xl font-bold text-gray-900">{summary.total.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 mb-1">총 이미지</div>
                      </div>
                      {(() => {
                        const { trainPct, valPct, testPct } = computeSplitPercents(summary.total, summary.train, summary.val, summary.test)
                        return (
                          <div className="mt-3">
                            <div className="h-3 w-full bg-gray-200 rounded overflow-hidden flex">
                              <div className="h-full bg-green-600" style={{ width: `${trainPct}%` }}></div>
                              <div className="h-full bg-blue-500" style={{ width: `${valPct}%` }}></div>
                              <div className="h-full bg-yellow-500" style={{ width: `${testPct}%` }}></div>
                            </div>
                            <div className="mt-2 flex gap-3 text-xs">
                              <span className="inline-flex items-center gap-1 text-green-700"><span className="w-2 h-2 rounded-full bg-green-600"></span>Train {summary.train}</span>
                              <span className="inline-flex items-center gap-1 text-blue-700"><span className="w-2 h-2 rounded-full bg-blue-500"></span>Val {summary.val}</span>
                              <span className="inline-flex items-center gap-1 text-yellow-700"><span className="w-2 h-2 rounded-full bg-yellow-500"></span>Test {summary.test}</span>
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* 라벨/원천/옵션/메타 (단일 컬럼, 아래로 넉넉히) */}
                    <div className="space-y-3 text-sm text-gray-700">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5">라벨:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                          {(summary.label_version_path || summary.label_version_name || '-') + (summary.label_archive_name ? ` > ${summary.label_archive_name}` : '')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>라벨 형식:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{summary.label_format || '-'}</span>
                      </div>
                      <div>
                        <div className="mb-1">원천:</div>
                        {renderSourcePills(summary.raw_sources)}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>샘플링:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{renderSampling(summary.sampling)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>출력:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{renderOutput(summary.output)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>ZIP 파일:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                          {summary.zip_ready && summary.zip_size != null ? formatBytes(summary.zip_size) : '아직 없음'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>생성자:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">{summary.created_by_name ? `${summary.created_by_name}${summary.created_by_email ? ` (${summary.created_by_email})` : ''}` : (summary.created_by_email || '-')}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>생성일:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs">{formatLocalDate(summary.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>ZIP 보관 기한:</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">{summary.zip_expires_at ? formatLocalDate(summary.zip_expires_at) : '아직 없음'}</span>
                      </div>
                    </div>
                    </div>
                  )}
                </div>
              )}

              {/* DVC 탭 */}
              {latest?.ready && activeTab === 'dvc' && (
                <div className="mt-4">
                  <div className="mt-6 border rounded p-6">
                    <div className="text-base font-semibold text-gray-900 mb-4">DVC 정보</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-gray-500 w-28 flex-shrink-0">콘텐츠 해시</span>
                        {dvcContentHash
                          ? <code className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs break-all">{dvcContentHash}</code>
                          : <span className="text-gray-400 text-xs">스토어에 보존되지 않은 버전입니다 (DVC 도입 이전 빌드)</span>}
                      </div>
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-gray-500 w-28 flex-shrink-0">기반 버전</span>
                        {lineage?.parent
                          ? <span className="text-gray-900">{lineage.parent.name}</span>
                          : <span className="text-gray-400">없음</span>}
                      </div>
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-gray-500 w-28 flex-shrink-0">보관 방식</span>
                        <span className="text-gray-700 text-xs">
                          DVC 스토어에 보관됨
                        </span>
                      </div>
                    </div>
                  </div>
                  </div>
              )}

              {/* 변경 파일 탭 */}
              {latest?.ready && activeTab === 'changes' && (
                <div className="mt-4 space-y-4">
                          <div className="border rounded p-4">
                            <div className="text-sm font-semibold text-gray-900 mb-3">버전 비교</div>
                            {comparableVersions.length === 0 ? (
                              <div className="text-xs text-gray-500">비교할 다른 버전이 없습니다.</div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-gray-600">기준 버전</span>
                                  <select
                                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                    value={compare.baseId ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value ? Number(e.target.value) : null
                                      if (v) runCompare(v)
                                      else setCompare({ baseId: null, loading: false, result: null })
                                    }}
                                  >
                                    <option value="">선택하세요</option>
                                    {comparableVersions.map(v => (
                                      <option key={v.id} value={v.id}>
                                        {v.name}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="text-xs text-gray-500">→ {selected?.name}</span>
                                </div>

                                {compare.loading && <div className="mt-3 text-xs text-gray-500">비교 중...</div>}
                                {compare.error && <div className="mt-3 text-xs text-red-600">{compare.error}</div>}
                                {compare.result && (
                                  <div className="mt-3 space-y-3">
                                    {compare.result.reason && !compare.result.diff && (
                                      <div className="text-xs text-gray-500">{compare.result.reason}</div>
                                    )}
                                    {/* 파일 단위 */}
                                    {compare.result.diff && (
                                      <div className="flex flex-wrap gap-1 text-xs">
                                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">+{compare.result.diff.added.image_count}장 추가</span>
                                        <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">−{compare.result.diff.removed.image_count}장 제외</span>
                                        {compare.result.diff.modified.image_count > 0 && (
                                          <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">{compare.result.diff.modified.image_count}장 변경</span>
                                        )}
                                        {compare.result.diff.added.label_count > 0 && (
                                          <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800">라벨 추가 {compare.result.diff.added.label_count}건</span>
                                        )}
                                        {compare.result.diff.removed.label_count > 0 && (
                                          <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800">라벨 제외 {compare.result.diff.removed.label_count}건</span>
                                        )}
                                        {compare.result.diff.modified.label_count > 0 && (
                                          <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-800">라벨 수정 {compare.result.diff.modified.label_count}건</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                  {compare.result?.diff && (
                    <div className="border rounded p-4">
                      <div className="text-sm font-semibold text-gray-900 mb-2">변경된 파일</div>
                      {(['added', 'removed', 'modified'] as const).map(k => {
                        const part = compare.result!.diff![k]
                        const label = k === 'added' ? '추가' : k === 'removed' ? '제외' : '변경'
                        const files = [...(part.image_samples || []), ...(part.label_samples || [])]
                        if (files.length === 0) return null
                        return (
                          <div key={k} className="mt-2">
                            <div className="text-xs font-medium text-gray-700 mb-1">
                              {label} · 이미지 {part.image_count}건 / 라벨 {part.label_count}건
                            </div>
                            <ul className="text-xs text-gray-600 font-mono max-h-40 overflow-y-auto border rounded px-2 py-1 bg-gray-50">
                              {files.slice(0, 60).map(p => <li key={p} className="truncate">{p}</li>)}
                            </ul>
                            {files.length > 60 && (
                              <div className="text-xs text-gray-400 mt-0.5">…외 {files.length - 60}건 (샘플만 표시)</div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {/* 라벨 단위 비교 — 백그라운드 계산이라 늦게 도착한다.
                      기존 내용을 밀어내지 않도록 「변경된 파일」 아래에 두고,
                      계산 중에는 결과와 비슷한 높이의 자리를 미리 잡아둔다. */}
                  {compare.result && compare.result.label_diff_status !== 'none' && (
                    <div className="border rounded p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-900">클래스별 증감</div>
                        {(compare.result.label_diff_status === 'queued' || compare.result.label_diff_status === 'running') && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <Spinner className="h-3.5 w-3.5 text-gray-500" />
                            라벨 파일을 읽는 중
                          </span>
                        )}
                      </div>

                      {(compare.result.label_diff_status === 'queued' || compare.result.label_diff_status === 'running') && (
                        <div>
                          <div className="text-xs text-gray-500 mb-2">
                            변경된 라벨 파일을 하나씩 파싱합니다. 마스크가 많으면 수십 초 걸릴 수 있습니다.
                          </div>
                          {/* 완료 시 레이아웃이 튀지 않도록 결과와 비슷한 높이를 미리 확보 */}
                          <div className="space-y-1.5" aria-hidden="true">
                            {[0, 1, 2, 3].map(i => (
                              <div key={i} className="flex items-center gap-2">
                                {/* gray-100 은 animate-pulse 의 투명도 변화가 거의 안 보여
                                    선처럼만 보였다 → gray-300 으로 진하게 */}
                                <div className="h-3.5 bg-gray-300 rounded animate-pulse" style={{ width: `${28 - i * 3}%` }} />
                                <div className="h-3.5 bg-gray-300 rounded animate-pulse flex-1" />
                                <div className="h-3.5 w-12 bg-gray-300 rounded animate-pulse" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {compare.result.label_diff_status === 'failed' && (
                        <div className="text-xs text-red-600">라벨 비교 실패: {compare.result.reason}</div>
                      )}

                      {compare.result.label_diff?.sections?.map((sec, si) => (
                        <div key={sec.label_kind} className={si > 0 ? 'border-t pt-2 mt-2' : ''}>
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {sec.label_kind.toUpperCase()} · {sec.unit === 'pixels' ? '클래스별 픽셀' : '클래스별 어노테이션'}
                          </div>
                          {sec.per_class.length === 0 ? (
                            <div className="text-xs text-gray-500">변경된 클래스가 없습니다.</div>
                          ) : (
                            <table className="w-full text-xs">
                              <tbody>
                                {sec.per_class.slice(0, 12).map(c => (
                                  <tr key={c.raw_key} className="border-b last:border-0">
                                    <td className="py-0.5 text-gray-700">{c.class}</td>
                                    <td className="py-0.5 text-right text-gray-500">{c.base.toLocaleString()} → {c.target.toLocaleString()}</td>
                                    <td className={`py-0.5 text-right w-20 font-medium ${
                                      c.delta > 0 ? 'text-green-700' : c.delta < 0 ? 'text-red-700' : 'text-gray-400'
                                    }`}>
                                      {c.delta > 0 ? '+' : ''}{c.delta.toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))}

                      {compare.result.label_diff?.files?.truncated && (
                        <div className="text-xs text-amber-700 mt-2">
                          변경 파일이 많아 {compare.result.label_diff.files.max_files.toLocaleString()}건까지만 집계했습니다
                          (전체 {compare.result.label_diff.files.changed.toLocaleString()}건).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 지표/통계 탭 */}
              {latest?.ready && activeTab === 'stats' && (
                <div className="mt-4 space-y-4">
                  {summary && (
                    <div className="border rounded p-6">
                      <div className="text-base font-semibold text-gray-900 mb-3">분할</div>
                      <div className="text-sm text-gray-700">
                        총 이미지 <span className="font-semibold text-gray-900">{summary.total.toLocaleString()}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        Train {summary.train.toLocaleString()} · Val {summary.val.toLocaleString()} · Test {summary.test.toLocaleString()}
                      </div>
                    </div>
                  )}

                  <div className="border rounded p-6">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-base font-semibold text-gray-900">클래스별 분포</div>
                      {classStats?.status === 'completed' && classStats.files && (
                        <div className="text-xs text-gray-400">
                          라벨 파일 {classStats.files.parsed.toLocaleString()}/{classStats.files.labels.toLocaleString()}건 집계
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      라벨 파일을 직접 파싱하므로 Detection · Segmentation · Classification 모두 집계됩니다.
                    </div>

                    {!classStats && <div className="text-sm text-gray-500">불러오는 중…</div>}
                    {(classStats?.status === 'queued' || classStats?.status === 'running') && (
                      <div className="text-sm text-gray-500">라벨 파일을 집계하는 중입니다…</div>
                    )}
                    {classStats?.status === 'none' && (
                      <div className="text-sm text-gray-500">{classStats.reason}</div>
                    )}
                    {classStats?.status === 'failed' && (
                      <div className="text-sm text-red-600">집계 실패: {classStats.reason}</div>
                    )}
                    {classStats?.status === 'completed' && classStats.sections.length === 0 && (
                      <div className="text-sm text-gray-500">인식할 수 있는 라벨 파일이 없습니다.</div>
                    )}

                    {classStats?.status === 'completed' && classStats.sections.map(sec => (
                      <div key={sec.label_kind} className="mt-4">
                        <div className="text-sm font-medium text-gray-800 mb-1">
                          {sec.label_kind.toUpperCase()} · {sec.unit === 'pixels' ? '클래스별 픽셀' : '클래스별 어노테이션'}
                          <span className="ml-2 text-xs text-gray-500">합계 {sec.total.toLocaleString()}</span>
                        </div>
                        <table className="min-w-full text-xs border border-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1 border-b text-left font-medium text-gray-700">클래스</th>
                              <th className="px-2 py-1 border-b text-right font-medium text-gray-700">{sec.unit === 'pixels' ? '픽셀' : '개수'}</th>
                              <th className="px-2 py-1 border-b text-right font-medium text-gray-700 w-16">비율</th>
                              <th className="px-2 py-1 border-b font-medium text-gray-700 w-40">분포</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sec.per_class.map(c => (
                              <tr key={c.raw_key} className="odd:bg-white even:bg-gray-50">
                                <td className="px-2 py-1 border-b border-gray-100 text-gray-800">{c.class}</td>
                                <td className="px-2 py-1 border-b border-gray-100 text-right text-gray-900">{c.count.toLocaleString()}</td>
                                <td className="px-2 py-1 border-b border-gray-100 text-right text-gray-500">{c.ratio}%</td>
                                <td className="px-2 py-1 border-b border-gray-100">
                                  <div className="h-2 bg-gray-200 rounded overflow-hidden">
                                    <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, c.ratio)}%` }} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                    {classStats?.status === 'completed' && classStats.files?.truncated && (
                      <div className="text-xs text-amber-700 mt-2">
                        라벨 파일이 많아 {classStats.files.max_files.toLocaleString()}건까지만 집계했습니다.
                      </div>
                    )}
                  </div>

                  {summary?.per_class_counts && Object.keys(summary.per_class_counts).length > 0 && (
                    <div className="border rounded p-6">
                      <div className="text-base font-semibold text-gray-900 mb-1">빌드 기록 (split 별)</div>
                      <div className="text-xs text-gray-500 mb-3">빌드 시점에 기록된 Classification 분포입니다.</div>
          {summary.per_class_counts && typeof summary.per_class_counts === 'object' && Object.keys(summary.per_class_counts).length > 0 && (
            <div className="mt-4">
              <div className="mb-1 font-medium text-gray-800 text-sm">클래스별 개수 분포</div>
              <div className="overflow-x-auto">
                {(() => {
                  const pcc = summary.per_class_counts as any
                  const splits = ['train', 'val', 'test'] as const
                  const totalMap = pcc['__total'] && typeof pcc['__total'] === 'object' ? pcc['__total'] : {}
                  const classNames = Array.from(
                    new Set<string>([
                      ...Object.keys(totalMap || {}),
                      ...splits.flatMap(s => Object.keys((pcc[s] || {}) as any)),
                    ]),
                  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                  if (classNames.length === 0) return null
                  return (
                    <table className="min-w-full text-xs border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 border-b border-gray-200 text-left font-medium text-gray-700">클래스</th>
                          <th className="px-2 py-1 border-b border-gray-200 text-right font-medium text-green-700">Train</th>
                          <th className="px-2 py-1 border-b border-gray-200 text-right font-medium text-blue-700">Val</th>
                          <th className="px-2 py-1 border-b border-gray-200 text-right font-medium text-yellow-700">Test</th>
                          <th className="px-2 py-1 border-b border-gray-200 text-right font-medium text-gray-800">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classNames.map(cls => {
                          const tr = (pcc.train && pcc.train[cls]) || 0
                          const va = (pcc.val && pcc.val[cls]) || 0
                          const te = (pcc.test && pcc.test[cls]) || 0
                          const tot = (totalMap && totalMap[cls]) || (tr + va + te)
                          return (
                            <tr key={cls} className="odd:bg-white even:bg-gray-50">
                              <td className="px-2 py-1 border-b border-gray-100 text-gray-800">{cls}</td>
                              <td className="px-2 py-1 border-b border-gray-100 text-right text-green-700">{tr}</td>
                              <td className="px-2 py-1 border-b border-gray-100 text-right text-blue-700">{va}</td>
                              <td className="px-2 py-1 border-b border-gray-100 text-right text-yellow-700">{te}</td>
                              <td className="px-2 py-1 border-b border-gray-100 text-right text-gray-900 font-medium">{tot}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )
                })()}
              </div>
            </div>
          )}
                    </div>
                  )}
                </div>
              )}

              {/* 다운로드/사용 탭 */}
              {/* 다운로드 탭 */}
              {latest?.ready && activeTab === 'download' && (
                <div className="mt-4 border rounded p-6 space-y-5">
                  <div className="text-base font-semibold text-gray-900">다운로드</div>

                  {/* 라벨 폭(w-36)을 세 줄 모두 같게 둔다 — 다르면 값 시작 위치가 어긋난다 */}
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 w-36 flex-shrink-0">ZIP 파일</span>
                      <span className="text-gray-900">
                        {summary?.zip_ready && summary.zip_size != null
                          ? formatBytes(summary.zip_size)
                          : '다운로드 시 생성'}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 w-36 flex-shrink-0">보관 기한</span>
                      <span className="text-gray-900">
                        {summary?.zip_expires_at
                          ? formatLocaleDateOnly(summary.zip_expires_at)
                          : `ZIP 생성 후 ${summary?.zip_retention_days || 0}일`}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 w-36 flex-shrink-0">ZIP 구성</span>
                      <span className="text-gray-700 text-xs">
                        이미지 · 라벨 · <code className="px-1 rounded bg-gray-100">meta/manifest.json</code> ·
                        <code className="px-1 rounded bg-gray-100">meta/options.json</code> 이 담긴 ZIP 한 개
                      </span>
                    </div>
                  </div>

                  {/* ZIP 이 없을 때만: 데이터는 남아 있고 ZIP 만 필요할 때 만든다는 점 */}
                  {!summary?.zip_ready && (
                    <p className="text-xs text-gray-500">
                      데이터셋은 계속 보관됩니다. 다운로드용 ZIP 파일은 필요할 때 생성되어
                      {` ${summary?.zip_retention_days || 0}일간 `}
                      보관되며, 새로 준비하는 경우 완료 후 자동으로 다운로드됩니다.
                    </p>
                  )}

                  <div className="border-t pt-4">
                    {canDownloadSelected && (
    <button
      className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      disabled={isDownloading || isRestoring}
      onClick={async () => {
        if (!selected) return
        try {
          const suggested = `${(selected.name || 'dataset')}`
          const input = window.prompt('다운로드할 파일명을 입력하세요 (확장자 없이)', suggested)
          if (input === null) return
          const cleaned = (input || '').trim().replace(/[\\/\0]/g, '')
          if (cleaned.length === 0) {
            alert('유효한 파일명을 입력하세요.')
            return
          }
          setIsDownloading(true)
          const res = await datasetService.download(selected.id, cleaned)
          if (res.status === 'processing') {
            // ZIP 캐시가 만료·삭제됨 → 스토어에서 재조립 중.
            // 전역 감시자가 완료를 지켜보다 알림 + 자동 다운로드한다 (화면을 떠나도 유지)
            startRestoreTracking(selected.id, selected.name || 'dataset', cleaned, res.polling_url)
          }
        } catch (e) {
          alert((e as Error)?.message || '데이터셋 다운로드에 실패했습니다.')
        } finally {
          window.setTimeout(() => setIsDownloading(false), 1200)
        }
      }}
    >
      {/* 상태가 바뀌어도 버튼 폭이 흔들리지 않게 최소 폭을 두고 가운데 정렬한다.
          스피너만 있을 때 글자용 여백(mr-2)이 남아 왼쪽으로 밀리던 문제도 여기서 없앤다. */}
      <span className="inline-flex items-center justify-center gap-1.5 min-w-[7.5rem]">
        {(isRestoring || isDownloading) && <Spinner className="h-4 w-4 text-gray-700" />}
        {isRestoring
          ? (restoreProgress != null ? `다운로드 파일 준비 중… ${restoreProgress}%` : '다운로드 파일 준비 중…')
          : isDownloading ? '내려받는 중…' : '다운로드'}
      </span>
    </button>
                    )}
                    {/* 진행 중일 때만 보여준다 — 평상시엔 위 안내 한 문단으로 충분하다 */}
                    {isRestoring && (
                      <div className="text-xs text-gray-500 mt-2">
                        다운로드 파일을 준비하고 있습니다. 완료되면 자동으로 다운로드됩니다.
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
          </div>
        </div>

        {/* ── 3열: 버전 히스토리 + 빠른 작업 ───────────────────────
            높이를 따로 주지 않는다 — 2열(헤더+본문)이 정한 행 높이에 stretch 로
            맞춰야 열마다 바닥이 어긋나지 않는다 */}
        {selected && isDatasetLeaf(selected) && (
          <div className="w-80 flex-shrink-0 border-l border-gray-200 bg-gray-50 overflow-y-auto p-3 space-y-3">
            <div className="bg-white rounded">
            <div className="border rounded p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">버전 히스토리</div>
              {siblingVersions.length <= 1 && (
                <div className="text-xs text-gray-500 mb-2">
                  독립 데이터셋입니다 — 이전 버전을 선언하지 않아 계보가 없습니다.
                </div>
              )}
              <VersionTimeline
                versions={siblingVersions}
                currentId={currentVersion?.id ?? null}
                selectedId={selected?.id ?? null}
                layout="stacked"
                onSelect={(v) => { setSelected(v); setDescExpanded(false) }}
              />
            </div>
            </div>
            <div className="bg-white border rounded p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">빠른 작업</div>
              <div className="space-y-2">
                {/* 주 동작: 채운 버튼 + 아이콘 */}
                {canDownloadSelected && (
                  <button
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 disabled:opacity-60"
                    disabled={isDownloading || isRestoring}
                    onClick={handleDownload}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    {isRestoring ? (restoreProgress != null ? `다운로드 파일 준비 중… ${restoreProgress}%` : '다운로드 파일 준비 중…') : isDownloading ? '내려받는 중…' : 'ZIP 다운로드'}
                  </button>
                )}

                {/* 비교: 어떤 버전과 비교하는지 문장으로 드러낸다 */}
                {siblingVersions.filter(v => v.id !== selected.id).length > 0 && (
                  <div className="border border-gray-200 rounded px-3 py-2.5">
                    <div className="text-xs text-gray-600 mb-1.5">
                      <span className="font-medium text-gray-900">{selected.name}</span> 을(를) 어떤 버전과 비교할까요?
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        className="flex-1 min-w-0 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                        value={compare.baseId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value ? Number(e.target.value) : null
                          setCompare({ baseId: v, loading: false, result: null })
                        }}
                      >
                        <option value="">버전 선택</option>
                        {comparableVersions.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                      <button
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-40 flex-shrink-0"
                        disabled={!compare.baseId}
                        onClick={() => { if (compare.baseId) { setActiveTab('changes'); runCompare(compare.baseId) } }}
                      >
                        비교
                      </button>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">결과는 「변경 파일」 탭에 표시됩니다</div>
                  </div>
                )}
              </div>

              {/* 되돌릴 수 없는 동작 — 구분선 아래 + 빨간 외곽선으로 구별한다
                  (주 동작인 다운로드는 채운 검정이라 색으로 확실히 갈린다) */}
              {canDeleteSelected && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <button
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm border border-red-300 text-red-700 rounded bg-white hover:bg-red-50 hover:border-red-400 disabled:opacity-60"
                    disabled={isDeleting}
                    onClick={() => { if (selected) setConfirmDelete({ open: true, node: selected, typed: '' }) }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {isDeleting ? '삭제 중…' : '데이터셋 삭제'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <CreateDatasetProjectModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateProject}
        checkDuplicate={checkDuplicateLocal}
        canCreateNewProject={hasGlobalDatasetProjectManage}
      />

        <CreateDatasetModal
        isOpen={!!buildTarget}
        mode={buildTarget?.mode ?? 'rebuild'}
        datasetNodeId={buildTarget?.mode === 'rebuild' ? buildTarget.nodeId : 0}
        siblingDatasets={buildSiblings}
        initialParentNodeId={buildTarget?.mode === 'rebuild' ? (lineage?.parent?.id ?? null) : undefined}
        onClose={() => setBuildTarget(null)}
        onSubmit={async (payload: CreateDatasetPayload) => {
          const target = buildTarget
          // create 모드: 빌드 완료 후 트리를 갱신하고 새 데이터셋 노드를 선택
          const refreshAfterCreate = async (nodeId: number, t: { projectId: number; taskId: number }) => {
            const list = await loadProjects()
            setExpanded(prev => { const ns = new Set(prev); ns.add(`project-${t.projectId}`); ns.add(`task-${t.taskId}`); return ns })
            const ds = list.find(n => n.kind === 'dataset' && n.id === nodeId && n.parent_kind === 'task' && n.parent_id === t.taskId) || null
            if (ds) { setSelected(ds as any); setDescExpanded(false) }
          }
          // 진행 표시·폴링은 catch 의 승인 재시도에서도 같은 함수를 써야 하므로
          // try 밖에 둔다 (안에 두면 catch 에서 보이지 않는다).
          let targetNodeId = payload.datasetNodeId
          // 빌드 응답 처리 — 진행 표시와 폴링. 승인 재시도도 이 함수를 쓴다.
          // 예전에는 재시도 쪽에 폴링을 안 걸어 진행 창이 "대기 중"에서 멈췄다.
          const handleBuildResponse = async (res: any) => {
            if (res.ready && res.download_url) {
              alert('✅ 캐시된 데이터셋이 준비되어 즉시 다운로드합니다.')
              window.location.href = res.download_url
              if (target?.mode === 'create') {
                try { await refreshAfterCreate(targetNodeId, target) } catch {}
              }
            } else if (res.job_id) {
              setActiveJob({ jobId: Number(res.job_id), status: 'queued' })
              setProgressOpen(true)
              // 폴링 시작 (모달 닫힘/실패 시 중단)
              pollStopRef.current = false
              const poll = async () => {
                if (pollStopRef.current || !res.job_id) return
                try {
                  const st = await datasetService.getJobStatus(res.job_id)
                  setActiveJob({ jobId: Number(res.job_id), status: st.status, step: st.step, progress: st.progress, download_url: st.download_url, error: st.error })
                  if (st.status === 'completed') {
                    // 완료 신호를 받은 직후에는 DB/파일 시스템이 완전히 반영되기까지 약간의 시간차가 있을 수 있으므로
                    // 짧은 지연(예: 500ms)을 둔 뒤 latest/summary를 조회한다.
                    pollStopRef.current = true
                    window.setTimeout(async () => {
                      try {
                        const latestSummary = await datasetService.getLatestSummary(targetNodeId)
                        setLatest(latestSummary)
                        try { setLineage(await datasetService.getLineage(targetNodeId)) } catch {}
                      } catch {
                        setLatest(null)
                      }
                      try {
                        const s = await datasetService.getSummary(targetNodeId)
                        setSummary(s)
                      } catch {
                        setSummary(null)
                      }
                      if (target?.mode === 'create') {
                        try { await refreshAfterCreate(targetNodeId, target) } catch {}
                      }
                      setProgressOpen(false)
                      setActiveJob(null)
                    }, 500)
                    return
                  }
                  if (st.status === 'failed') {
                    pollStopRef.current = true
                    return
                  }
                } catch {}
                if (!pollStopRef.current) {
                  pollTimerRef.current = window.setTimeout(poll, 2000)
                }
              }
              pollTimerRef.current = window.setTimeout(poll, 1500)
            } else {
              alert('요청이 접수되었습니다.')
            }
          }
          try {
            // ── 순서가 중요하다: **아무것도 만들기 전에 먼저 묻는다.** ──
            //
            // 예전에는 데이터셋 노드를 먼저 만들고 물어봤다. 그래서 사용자가
            // 취소해도 빈 데이터셋이 트리에 남았다 (2026-09-07).
            //
            // 비식별 처리를 끄고 올린 사진이 섞여 있으면 개수를 보여주고 승인을
            // 받는다 — 얼굴·번호판이 애초에 없는 자료도 있어서 무조건 막지 않고,
            // 조용히 넣지도 않는다. 격리·실패분은 승인 대상이 아니며 서버가 제외한다.
            let approved = false
            try {
              const pre = await datasetService.buildPreflight(
                // 새로 만들기 흐름은 아직 노드가 없다 — 프로젝트로 물어본다
                target?.mode === 'create'
                  ? { ...payload, datasetNodeId: undefined, dataset_node_id: undefined,
                      dataset_project_id: target.projectId }
                  : payload
              )
              if (pre.buildable <= 0) {
                alert(
                  `데이터셋에 넣을 수 있는 사진이 없습니다.\n\n` +
                  `라벨이 붙은 사진 ${pre.total}장 중 ${pre.blocked}장이 격리·실패·기록없음 상태입니다.`
                )
                return
              }
              if (pre.needs_approval) {
                approved = window.confirm(
                  `비식별 처리를 하지 않고 올린 사진이 ${pre.skipped}장 있습니다.\n` +
                  `(업로드할 때 얼굴·번호판 지우기를 끈 사진입니다)\n\n` +
                  `포함하면 데이터셋에 ${pre.buildable}장, 제외하면 ${pre.stamped}장이 들어갑니다.\n\n` +
                  `포함하시겠습니까? 승인 기록은 이 데이터셋에 남습니다.`
                )
                if (!approved) return   // ← 여기서 나가면 만들어진 것이 하나도 없다
              }
            } catch (e) {
              // 미리 확인이 안 되면(구버전 서버 등) 그냥 진행한다 — 서버가 다시 막는다
              console.warn('비식별 상태 미리 확인 실패', e)
            }

            // 승인까지 끝난 뒤에 만든다
            if (target?.mode === 'create' && payload.datasetName) {
              const created = await datasetService.createDatasetNode(target.projectId, {
                name: payload.datasetName,
                taskId: target.taskId,
              })
              targetNodeId = created.dataset_id
              payload = { ...payload, datasetNodeId: created.dataset_id }
            }
            const res = await datasetService.buildDataset(
              approved ? { ...payload, allow_unanonymized: true } : payload
            )
            await handleBuildResponse(res)
          } catch (e: any) {
            // 서버가 사유를 담아 거절한 경우는 그 문장을 그대로 보여준다
            // (비식별 안 된 사진 승인 필요 / 넣을 사진 없음). 미리 확인이 실패해
            // 승인 없이 요청한 경우가 여기로 온다.
            if (e?.code === 'unanonymized_images_need_approval' && e?.preflight) {
              const pre = e.preflight
              const ok = window.confirm(
                `비식별 처리를 하지 않고 올린 사진이 ${pre.skipped}장 있습니다.\n\n` +
                `포함하면 ${pre.buildable}장, 제외하면 ${pre.stamped}장이 들어갑니다.\n\n` +
                `포함하시겠습니까?`
              )
              if (ok) {
                try {
                  await handleBuildResponse(
                    await datasetService.buildDataset({ ...payload, allow_unanonymized: true })
                  )
                } catch (e2: any) {
                  alert(e2?.message || '데이터셋 생성 요청에 실패했습니다.')
                }
              }
              return
            }
            alert(e?.message || '데이터셋 생성 요청에 실패했습니다.')
          } finally {
            setBuildTarget(null)
          }
        }}
      />

      <DatasetProgressModal
        isOpen={isProgressOpen}
        onClose={() => { setProgressOpen(false); pollStopRef.current = true; if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null } }}
        status={{ status: activeJob?.status || 'queued', step: activeJob?.step, progress: activeJob?.progress, error: activeJob?.error }}
      />

      {/* 모델 생성 모달 */}
      {taskModal.open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="text-lg font-semibold text-gray-900 mb-4">작업 생성</div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-700 mb-1">작업 이름 <span className="text-red-500 ml-0.5">*</span></label>
                <input
                  autoFocus
                  type="text"
                  value={taskModal.name}
                  onChange={(e) => setTaskModal(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      await handleCreateTask()
                    } else if (e.key === 'Escape') {
                      setTaskModal({ open: false, projectId: 0, name: '', description: '', submitting: false })
                    }
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="작업 이름을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">설명 (선택)</label>
                <textarea
                  value={taskModal.description}
                  onChange={(e) => setTaskModal(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  rows={3}
                  placeholder="작업 설명을 입력하세요"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                onClick={() => setTaskModal({ open: false, projectId: 0, name: '', description: '', submitting: false })}
              >
                취소
              </button>
              <button
                className={`px-3 py-1 text-sm rounded text-white ${taskModal.name.trim().length > 0 && !taskModal.submitting ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'}`}
                disabled={taskModal.name.trim().length === 0 || taskModal.submitting}
                onClick={handleCreateTask}
              >
                {taskModal.submitting ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 데이터셋 삭제 확인 모달 */}
      {confirmProjectDelete.open && confirmProjectDelete.project && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">프로젝트 삭제</div>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-gray-700">
                '{confirmProjectDelete.project.name}' 프로젝트를 삭제하시겠습니까?
              </p>
              {(() => {
                const pid = confirmProjectDelete.project!.id
                const proj = confirmProjectDelete.project!
                const tasks = childrenOfContainer(nodes, proj, 'task')
                const dsets = nodes.filter(n => n.kind === 'dataset' && (
                  isChildOf(n, proj) || tasks.some(tk => isChildOf(n, tk))
                ))
                return (
                  <div className="text-sm bg-red-50 border border-red-200 rounded px-3 py-2 text-red-900">
                    <div>하위 <span className="font-semibold">작업 {tasks.length}개</span>,
                      <span className="font-semibold"> 데이터셋 {dsets.length}개</span>가 함께 삭제됩니다.</div>
                    <div className="text-xs text-red-700 mt-1">
                      DVC 스토어의 데이터는 지워지지 않고, 삭제 장부에 버전 해시가 기록됩니다.
                    </div>
                  </div>
                )
              })()}
              <div>
                <div className="text-xs text-gray-600 mb-2">
                  삭제를 확인하려면 <span className="font-semibold text-gray-900">{confirmProjectDelete.project.name}</span> 을(를) 입력하세요.
                </div>
                <input
                  autoFocus
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  placeholder={confirmProjectDelete.project.name}
                  value={confirmProjectDelete.typed}
                  onChange={(e) => setConfirmProjectDelete(prev => ({ ...prev, typed: e.target.value }))}
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                onClick={() => setConfirmProjectDelete({ open: false, project: null, typed: '' })}
              >
                취소
              </button>
              <button
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeletingProject || confirmProjectDelete.typed.trim() !== confirmProjectDelete.project.name}
                onClick={async () => {
                  const p = confirmProjectDelete.project
                  if (!p) return
                  setIsDeletingProject(true)
                  try {
                    await datasetService.deleteProject(p.id)
                    setConfirmProjectDelete({ open: false, project: null, typed: '' })
                    setSelected(null)
                    await loadProjects()
                  } catch (e) {
                    alert((e as Error)?.message || '프로젝트 삭제에 실패했습니다')
                  } finally {
                    setIsDeletingProject(false)
                  }
                }}
              >
                {isDeletingProject ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete.open && confirmDelete.node && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-3">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-base font-semibold text-gray-900">데이터셋 삭제</div>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-gray-700 select-text">
                '{confirmDelete.node.name}' 데이터셋을 영구적으로 삭제하시겠습니까?
              </p>
              <div>
                <div className="text-xs text-gray-600 mb-2">
                  삭제를 확인하려면 아래 텍스트 입력 필드에 <span className="font-semibold text-gray-900">{confirmDelete.node.name}</span> 을(를) 입력하세요.
                </div>
                <input
                  autoFocus
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  placeholder={confirmDelete.node.name}
                  value={confirmDelete.typed}
                  onChange={(e) => setConfirmDelete(prev => ({ ...prev, typed: e.target.value }))}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const required = confirmDelete.node?.name || ''
                      if (confirmDelete.typed.trim() !== required) return
                      try {
                        setIsDeleting(true)
                        const parentId = confirmDelete.node?.parent_id
                        const parentKind = confirmDelete.node?.parent_kind ?? 'project'
                        await datasetService.deleteDataset(confirmDelete.node!.id)
                        const list = await loadProjects()
                        if (parentId) {
                          const parent = list.find(n => n.kind === parentKind && n.id === parentId) || null
                          setSelected(parent as any)
                        } else {
                          setSelected(null)
                        }
                        setLatest(null)
                        setSummary(null)
                      } catch (e: any) {
                        alert(e?.message || '삭제에 실패했습니다.')
                      } finally {
                        setIsDeleting(false)
                        setConfirmDelete({ open: false, node: null, typed: '' })
                      }
                    } else if (e.key === 'Escape') {
                      setConfirmDelete({ open: false, node: null, typed: '' })
                    }
                  }}
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                className="text-sm px-3 py-1 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmDelete({ open: false, node: null, typed: '' })}
              >
                취소
              </button>
              <button
                className="text-sm px-3 py-1 rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={(confirmDelete.typed.trim() !== (confirmDelete.node?.name || '')) || isDeleting}
                onClick={async () => {
                  const required = confirmDelete.node?.name || ''
                  if (confirmDelete.typed.trim() !== required || !confirmDelete.node) return
                  try {
                    setIsDeleting(true)
                    const parentId = confirmDelete.node.parent_id
                    const parentKind = confirmDelete.node.parent_kind ?? 'project'
                    await datasetService.deleteDataset(confirmDelete.node.id)
                    const list = await loadProjects()
                    if (parentId) {
                      const parent = list.find(n => n.kind === parentKind && n.id === parentId) || null
                      setSelected(parent as any)
                    } else {
                      setSelected(null)
                    }
                    setLatest(null)
                    setSummary(null)
                  } catch (e: any) {
                    alert(e?.message || '삭제에 실패했습니다.')
                  } finally {
                    setIsDeleting(false)
                    setConfirmDelete({ open: false, node: null, typed: '' })
                  }
                }}
              >
                {isDeleting ? '삭제 중...' : '영구 삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

/**
 * 링 스피너 — 연한 전체 링(바깥선) 위를 진한 호가 따라 돈다.
 *
 * 두 번 틀렸던 부분이라 이유를 남긴다.
 *  - r=10 · strokeWidth=4 는 반지름 8~12 를 차지해 viewBox 24 안에서 네 방향이
 *    잘린다. r=9 · strokeWidth=3 은 7.5~10.5 라 여유가 있다.
 *  - CSS border 방식(border-t-*)은 네 변이 45° 로 만나 작은 크기에서 각지게
 *    돌아 보인다. 호를 그리려면 strokeDasharray 를 써야 한다.
 * 원주 = 2π×9 ≈ 56.5 → dashoffset 42 면 약 1/4 만 그려진다.
 */
function Spinner({ className = '' }: { className?: string }) {
  return (
    <svg
      role="status"
      aria-label="진행 중"
      viewBox="0 0 24 24"
      fill="none"
      className={`animate-spin flex-shrink-0 ${className}`}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <circle
        cx="12" cy="12" r="9"
        stroke="currentColor" strokeWidth="3" strokeLinecap="round"
        strokeDasharray="56.5" strokeDashoffset="42"
      />
    </svg>
  )
}
